import { Link } from 'react-router-dom';
import { useGetOnboardingQuery } from '@/store/configApi';
import {
  useGetCallsQuery,
  useGetDashboardQuery,
  useGetOrdersQuery
} from '@/store/api';
import { CallVolumeBar } from '@/components/ui/CallVolumeBar';
import { EmptyState, ErrorState, Panel, SkeletonRows, Status } from '@/components/ui';
import { formatDuration, formatMoney, formatPhone, formatTime } from '@/utils/format';

export function DashboardPage() {
  const dashboard = useGetDashboardQuery();
  const calls = useGetCallsQuery({ limit: 6 });
  const orders = useGetOrdersQuery({ limit: 5 });
  const onboarding = useGetOnboardingQuery();

  if (dashboard.isError) {
    return <ErrorState message="Could not load today's activity." onRetry={dashboard.refetch} />;
  }

  const stats = dashboard.data?.stats;
  const usage = dashboard.data?.usage;

  // Answered calls that did not become an order and were not transferred.
  const plainlyAnswered = Math.max(
    0,
    (stats?.answeredCalls ?? 0) - (stats?.phoneOrders ?? 0) - (stats?.transferredCalls ?? 0)
  );

  const setupIncomplete =
    onboarding.data && !onboarding.data.completedAt && !onboarding.data.readyForGoLive;

  return (
    <div className="space-y-6">
      {setupIncomplete ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber bg-amber-soft px-4 py-3">
          <p className="text-small text-ink">
            Your line is not answering calls yet. A few setup steps are still open.
          </p>
          <Link
            to="/setup"
            className="ml-auto rounded bg-ink px-3 py-1.5 text-small text-white hover:bg-ink-soft"
          >
            Finish setup
          </Link>
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
        <Panel>
          {dashboard.isLoading ? (
            <div className="px-4 py-8">
              <div className="h-8 w-24 rounded bg-rule" />
              <div className="mt-4 h-2.5 w-full rounded-sm bg-rule" />
            </div>
          ) : (
            <CallVolumeBar
              ordered={stats?.phoneOrders ?? 0}
              answered={plainlyAnswered}
              transferred={stats?.transferredCalls ?? 0}
              missed={stats?.missedCalls ?? 0}
            />
          )}
        </Panel>

        <Panel title="Phone orders today">
          <dl className="divide-y divide-rule">
            <Row label="Revenue" value={formatMoney(stats?.phoneOrderRevenueCents ?? 0)} />
            <Row label="Average order" value={formatMoney(stats?.averageOrderValueCents ?? 0)} />
            <Row
              label="Average call"
              value={formatDuration(stats?.averageCallDurationSeconds ?? 0)}
            />
          </dl>
        </Panel>
      </div>

      <Panel title="Minutes this cycle">
        <div className="px-4 py-4">
          <p className="text-small text-slate">
            <span className="tabular text-ink">
              {(usage?.usedMinutes ?? 0).toLocaleString()}
            </span>{' '}
            of{' '}
            <span className="tabular text-ink">
              {(usage?.includedMinutes ?? 0).toLocaleString()}
            </span>{' '}
            minutes used
          </p>

          <div className="mt-3 h-2 w-full overflow-hidden rounded-sm bg-rule">
            <div
              className={`h-full ${(usage?.usagePercent ?? 0) >= 90 ? 'bg-rust' : 'bg-jade'}`}
              style={{ width: `${Math.min(100, usage?.usagePercent ?? 0)}%` }}
            />
          </div>

          {usage && usage.overageMinutes > 0 ? (
            <p className="mt-3 text-small text-amber">
              {usage.overageMinutes.toLocaleString()} minutes over your plan, currently{' '}
              {formatMoney(usage.overageCostCents)} in extra charges.
            </p>
          ) : null}
        </div>
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel
          title="Recent calls"
          action={
            <Link to="/calls" className="text-small text-jade underline underline-offset-2">
              All calls
            </Link>
          }
        >
          {calls.isLoading ? (
            <SkeletonRows rows={5} />
          ) : (calls.data?.data.length ?? 0) === 0 ? (
            <EmptyState
              headline="No calls yet"
              hint="Once your number is live, every call shows up here with its transcript."
            />
          ) : (
            <ul className="divide-y divide-rule">
              {calls.data?.data.map((call) => (
                <li key={call._id}>
                  <Link
                    to={`/calls/${call._id}`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-paper"
                  >
                    <span className="tabular text-small text-slate">
                      {formatTime(call.startedAt)}
                    </span>
                    <span className="tabular text-small text-ink">
                      {formatPhone(call.callerNumber)}
                    </span>
                    <span className="ml-auto tabular text-small text-slate">
                      {formatDuration(call.durationSeconds)}
                    </span>
                    <Status value={call.status} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel
          title="Recent orders"
          action={
            <Link to="/orders" className="text-small text-jade underline underline-offset-2">
              All orders
            </Link>
          }
        >
          {orders.isLoading ? (
            <SkeletonRows rows={5} />
          ) : (orders.data?.data.length ?? 0) === 0 ? (
            <EmptyState
              headline="No phone orders yet"
              hint="Turn on ordering for a location and sync your menu to start taking orders by phone."
            />
          ) : (
            <ul className="divide-y divide-rule">
              {orders.data?.data.map((order) => (
                <li key={order._id} className="flex items-center gap-3 px-4 py-3">
                  <span className="tabular text-small text-slate">#{order.orderNumber}</span>
                  <span className="truncate text-small text-ink">
                    {order.customerName ?? 'Phone order'}
                  </span>
                  <span className="ml-auto tabular text-small text-ink">
                    {formatMoney(order.totalCents)}
                  </span>
                  <Status value={order.status} />
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-center justify-between px-4 py-3">
    <dt className="text-small text-slate">{label}</dt>
    <dd className="tabular text-small text-ink">{value}</dd>
  </div>
);
